import "../styles/EditTask.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { taskService } from "../../services/taskService";

// Tiptap і розширення
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";

// Плагін для зміни розміру зображень
import ResizeImage from "tiptap-extension-resize-image";

// Компоненти іконок та інтерфейсу
import { Button, TextInput, Tooltip, Menu, ActionIcon, Group, Paper, Title, Container } from "@mantine/core";
import { notifications } from '@mantine/notifications';
import {
    IconBold,
    IconItalic,
    IconUnderline,
    IconStrikethrough,
    IconCode,
    IconList,
    IconListNumbers,
    IconAlignLeft,
    IconAlignCenter,
    IconAlignRight,
    IconAlignJustified,
    IconH1,
    IconH2,
    IconH3,
    IconArrowBackUp,
    IconArrowForwardUp,
    IconChevronDown
} from "@tabler/icons-react";

const CreateTask = () => {
    const navigate = useNavigate();

    // Стан для заголовку
    const [title, setTitle] = useState("");

    // Створюємо редактор Tiptap з обробником вставки
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            TextStyle.configure({ types: [ListItem.name] }),
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            Link.configure({ openOnClick: false }),
            ResizeImage.configure({
                allowBase64: true
            })
        ],
        editorProps: {
            handlePaste: (view, event) => {
                // Отримуємо файли з буфера обміну
                const files = event.clipboardData?.files;

                // Якщо файлів немає, дозволяємо стандартну обробку
                if (!files || files.length === 0) {
                    return false;
                }

                // Знаходимо серед файлів лише зображення
                const imageFiles = Array.from(files).filter((file) =>
                    file.type.startsWith("image/")
                );

                // Якщо зображень немає, дозволяємо стандартну обробку
                if (imageFiles.length === 0) {
                    return false;
                }

                // Зупиняємо стандартну поведінку
                event.preventDefault();

                // Обробляємо кожне зображення
                imageFiles.forEach((file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const src = e.target.result; // Base64
                        editor.chain().focus().setImage({ src }).run();
                    };
                    reader.readAsDataURL(file);
                });

                // Повідомляємо, що ми обробили подію
                return true;
            }
        },
        content: ""
    });

    // Функція для застосування заголовків з перевіркою виділення
    const applyHeading = (level) => {
        if (!editor) return;
        const { state } = editor;
        const { selection } = state;
        if (selection.empty) {
            editor
                .chain()
                .focus()
                .splitBlock()
                .setNode("heading", { level })
                .run();
        } else {
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    // Зберігає задачу
    const saveTask = async () => {
        if (!editor || !title.trim()) {
            return notifications.show({
                title: 'Error',
                message: 'Заповніть заголовок і контент!',
                color: 'red'
            });
        }
        try {
            const contentHTML = editor.getHTML();
            await taskService.createTask(title, contentHTML);

            notifications.show({
                title: 'Success',
                message: 'Задача створена!',
                color: 'green'
            });
            navigate("/tasker");
        } catch (error) {
            console.error("Error creating task:", error);
            notifications.show({
                title: 'Error',
                message: 'Не вдалося створити задачу.',
                color: 'red'
            });
        }
    };

    return (
        <Container size="xl">
            <Title order={2} mb="md">Створення нової задачі</Title>

            <TextInput
                placeholder="Введіть заголовок"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                mb="md"
                size="md"
            />

            {editor && (
                <Paper withBorder p="xs" mb="sm" radius="md">
                    <Group gap={5}>
                        {/* Заголовки (Menu) */}
                        <Menu shadow="md" width={200}>
                            <Menu.Target>
                                <Button variant="subtle" size="xs" rightSection={<IconChevronDown size={14} />}>
                                    Heading
                                </Button>
                            </Menu.Target>

                            <Menu.Dropdown>
                                <Menu.Item leftSection={<IconH1 size={14} />} onClick={() => applyHeading(1)}>
                                    Heading 1
                                </Menu.Item>
                                <Menu.Item leftSection={<IconH2 size={14} />} onClick={() => applyHeading(2)}>
                                    Heading 2
                                </Menu.Item>
                                <Menu.Item leftSection={<IconH3 size={14} />} onClick={() => applyHeading(3)}>
                                    Heading 3
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>

                        {/* Основні стилі */}
                        <Tooltip label="Bold">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleBold().run()}>
                                <IconBold size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Italic">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleItalic().run()}>
                                <IconItalic size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Underline">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleUnderline().run()}>
                                <IconUnderline size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Strikethrough">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleStrike().run()}>
                                <IconStrikethrough size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Code">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleCode().run()}>
                                <IconCode size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Списки */}
                        <Tooltip label="Bullet list">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleBulletList().run()}>
                                <IconList size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Ordered list">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                                <IconListNumbers size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Вирівнювання */}
                        <Tooltip label="Align Left">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("left").run()}>
                                <IconAlignLeft size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Align Center">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("center").run()}>
                                <IconAlignCenter size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Align Right">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("right").run()}>
                                <IconAlignRight size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Justify">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
                                <IconAlignJustified size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Undo / Redo */}
                        <Tooltip label="Undo">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().undo().run()}>
                                <IconArrowBackUp size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Redo">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().redo().run()}>
                                <IconArrowForwardUp size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Paper>
            )}

            {/* Вміст редактора */}
            {editor && (
                <Paper withBorder p="md" radius="md" style={{ minHeight: '300px' }}>
                    <EditorContent editor={editor} />
                </Paper>
            )}

            {/* Кнопка створення задачі */}
            <Button mt="md" onClick={saveTask}>
                Створити задачу
            </Button>
        </Container>
    );
};

export default CreateTask;
